// Pure client-side status/degraded-copy logic for the AI chat dock (Task 4.1,
// AI_ASSISTANT_REMEDIATION_PLAN). Sentences here never include a dollar or
// token figure (SA10) so they stay correct regardless of the live ceiling.

import type { AiChatTranscriptMode, AssistantDailyRow } from "@checklist/shared";

export type ClientDegradedReason = "SITE_BUDGET" | "USER_BUDGET" | "MODEL_UNAVAILABLE";

/** Maps a chat stream's error code to a degraded reason, or null if it isn't one. */
export function degradedReasonForErrorCode(code: string): ClientDegradedReason | null {
  if (code === "SITE_BUDGET") return "SITE_BUDGET";
  if (code === "AI_CHAT_BUDGET") return "USER_BUDGET";
  if (
    code === "AI_MODEL_UNAVAILABLE" ||
    code === "AI_MODEL_BUSY" ||
    code === "AI_PROVIDER_KEY" ||
    code === "AI_PROVIDER_BILLING"
  ) {
    return "MODEL_UNAVAILABLE";
  }
  return null;
}

export function degradedSentence(reason: ClientDegradedReason): string {
  switch (reason) {
    case "SITE_BUDGET":
      return "The assistant has reached this month's usage limit.";
    case "USER_BUDGET":
      return "You've used today's assistant allowance. It resets at midnight UTC.";
    case "MODEL_UNAVAILABLE":
      return "The assistant can't answer right now.";
  }
}

export function transcriptNotice(mode: AiChatTranscriptMode): string {
  return mode === "metadata-only"
    ? "Only usage details are kept, not the text of conversations. Please don't share passwords or card numbers."
    : "Conversations are kept for 30 days to improve help. Please don't share passwords or card numbers.";
}

/** 1 - unresolved/conversations, a proxy the dashboard labels as such. Null when there is no volume. */
export function resolutionRate(rows: AssistantDailyRow[]): number | null {
  let conversations = 0;
  let unresolved = 0;
  for (const row of rows) {
    conversations += row.conversations ?? 0;
    unresolved += row.unresolvedConversations ?? 0;
  }
  if (conversations === 0) return null;
  return 1 - unresolved / conversations;
}
